import { CreatePaymentLinkParams, CreatePaymentLinkResponse, CustomRequestsResponse, ExperienceBookingsResponse, GetCustomRequestsParams, GetExperienceBookingsParams, GetExperiencesParams, PaymentLinkItem, UpdateBookingStatusParams, UpdateCustomRequestStatusParams } from "@/types/admin/experience-type";

// ✅ Match exactly how your other services (customerService, etc.) get the base URL.
//    If your admin-data.service.ts hardcodes the domain, do the same here.
const BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || "https://www.lilylanka.com";

function authHeaders(): HeadersInit {
  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("adminAccessToken")
      : null;
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export const experienceService = {
  /** GET /api/admin/experiences */
  async getExperiences(params: GetExperiencesParams) {
    const query = new URLSearchParams({
      page: String(params.page),
      pageSize: String(params.pageSize),
      ...(params.status ? { status: params.status } : {}),
      ...(params.search ? { search: params.search } : {}),
    });

    const res = await fetch(`${BASE_URL}/api/admin/experiences?${query}`, {
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error("Failed to fetch experiences");
    return res.json();
  },

  /** GET /api/admin/experiences/{experienceId} */
  async getExperienceById(id: string) {
    const res = await fetch(`${BASE_URL}/api/admin/experiences/${id}`, {
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error("Failed to fetch experience detail");
    return res.json();
  },

  /** GET /api/admin/experiences/{experienceId}/history */
  async getExperienceHistory(id: string) {
    const res = await fetch(
      `${BASE_URL}/api/admin/experiences/${id}/history`,
      { headers: authHeaders() }
    );
    if (!res.ok) throw new Error("Failed to fetch experience history");
    return res.json();
  },

  /** POST /api/admin/experiences/{experienceId}/start-review */
  async startReview(id: string, adminUserId: string) {
    const res = await fetch(
      `${BASE_URL}/api/admin/experiences/${id}/start-review?adminUserId=${adminUserId}`,
      { method: "POST", headers: authHeaders() }
    );
    if (!res.ok) throw new Error("Failed to start review");
    return res.json();
  },

  /** POST /api/admin/experiences/{experienceId}/approve */
  async approveExperience(id: string, adminUserId: string, notes: string) {
    const res = await fetch(
      `${BASE_URL}/api/admin/experiences/${id}/approve?adminUserId=${adminUserId}`,
      {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ notes }),
      }
    );
    if (!res.ok) throw new Error("Failed to approve experience");
    return res.json();
  },

  /** POST /api/admin/experiences/{experienceId}/request-changes */
  async requestChanges(id: string, adminUserId: string, notes: string) {
    const res = await fetch(
      `${BASE_URL}/api/admin/experiences/${id}/request-changes?adminUserId=${adminUserId}`,
      {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ notes }),
      }
    );
    if (!res.ok) throw new Error("Failed to request changes");
    return res.json();
  },

  /** POST /api/admin/experiences/{experienceId}/reject */
  async rejectExperience(id: string, adminUserId: string, reason: string) {
    const res = await fetch(
      `${BASE_URL}/api/admin/experiences/${id}/reject?adminUserId=${adminUserId}`,
      {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ reason }),
      }
    );
    if (!res.ok) throw new Error("Failed to reject experience");
    return res.json();
  },
  // ─── ADD THIS METHOD to your existing experienceService object ───────────────
// in: services/system-admin/experience.service.ts

  /** GET /api/admin/experiences/bookings */
  async getExperienceBookings(params: GetExperienceBookingsParams) {
    const query = new URLSearchParams({
      page: String(params.page),
      pageSize: String(params.pageSize),
      ...(params.status ? { status: params.status } : {}),
      ...(params.paymentStatus ? { paymentStatus: params.paymentStatus } : {}),
      ...(params.customerEmail ? { customerEmail: params.customerEmail } : {}),
      ...(params.experienceId ? { experienceId: params.experienceId } : {}),
    });

    const res = await fetch(
      `${BASE_URL}/api/admin/experiences/bookings?${query}`,
      { headers: authHeaders() }
    );
    if (!res.ok) throw new Error("Failed to fetch experience bookings");
    return res.json() as Promise<ExperienceBookingsResponse>;
  },
  async updateBookingStatus(params: UpdateBookingStatusParams) {
    const { bookingUid, ...body } = params;
    const res = await fetch(
      `${BASE_URL}/api/admin/experiences/${bookingUid}/status`,
      { method: "PATCH", headers: authHeaders(), body: JSON.stringify(body) }
    );
    if (!res.ok) throw new Error("Failed to update booking status");
    return res.json();
  },
  /** GET /api/admin/experiences/custom-requests */
  async getCustomRequests(params: GetCustomRequestsParams) {
    const query = new URLSearchParams({
      page: String(params.page),
      pageSize: String(params.pageSize),
      ...(params.status ? { status: params.status } : {}),
      ...(params.customerEmail ? { customerEmail: params.customerEmail } : {}),
      ...(params.experienceId ? { experienceId: params.experienceId } : {}),
      ...(params.fromDate ? { fromDate: params.fromDate } : {}),
      ...(params.toDate ? { toDate: params.toDate } : {}),
    });
    const res = await fetch(
      `${BASE_URL}/api/admin/experiences/custom-requests?${query}`,
      { headers: authHeaders() }
    );
    if (!res.ok) throw new Error("Failed to fetch custom requests");
    return res.json() as Promise<CustomRequestsResponse>;
  },

  /** PATCH /api/admin/experiences/custom-requests/{uid}/status */
  async updateCustomRequestStatus(params: UpdateCustomRequestStatusParams) {
    const { uid, ...body } = params;
    const res = await fetch(
      `${BASE_URL}/api/admin/experiences/custom-requests/${uid}/status`,
      { method: "PATCH", headers: authHeaders(), body: JSON.stringify(body) }
    );
    if (!res.ok) throw new Error("Failed to update custom request status");
    return res.json();
  },

  /** POST /api/partner/experience-bookings/{bookingUid}/payment-links */
  async createBookingPaymentLink(params: CreatePaymentLinkParams) {
    const { bookingUid, ...body } = params;
    const res = await fetch(
      `${BASE_URL}/api/admin/experience-bookings/${bookingUid}/payment-links`,
      { method: "POST", headers: authHeaders(), body: JSON.stringify(body) }
    );
    if (!res.ok) throw new Error("Failed to create payment link");
    return res.json() as Promise<CreatePaymentLinkResponse>;
  },

  /** GET /api/partner/experience-bookings/{bookingUid}/payment-links */
  async getBookingPaymentLinks(bookingUid: string) {
    const res = await fetch(
      `${BASE_URL}/api/admin/experience-bookings/${bookingUid}/payment-links`,
      { headers: authHeaders() }
    );
    if (!res.ok) throw new Error("Failed to fetch payment links");
    return res.json() as Promise<PaymentLinkItem[]>;
  },
};


